import { useState, useEffect, Fragment } from "react";
import { useDidUpdate } from "@/hooks/useDidUpdate";
import { todayISO } from "@/lib/utils";
import PageMeta from "@/components/common/PageMeta";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import { Modal } from "@/components/ui/modal";
import ConfirmModal from "@/components/ui/modal/ConfirmModal";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import Label from "@/components/form/Label";
import Input from "@/components/form/input/InputField";
import { feedProductionService } from "@/services/feedProductionService";
import { feedFormulaService } from "@/services/masterDataService";
import { handleApiError } from "@/services/api";
import DateRangeFilter, { getDefaultDateRange } from "@/components/common/DateRangeFilter";
import type {
  FeedProduction,
  FeedFormula,
} from "@/types/models";
import { useIsViewer } from "@/hooks/useRole";
import { useSortable } from "@/hooks/useSortable";
import SortableHeader from "@/components/ui/table/SortableHeader";
import Pagination from "@/components/ui/pagination/Pagination";
import { formatQty, formatRate, displayUnit } from "@/utils/formatQty";
import BatchPreview from "./BatchPreview";

const selectClass =
  "h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

export default function FeedProductionPage() {
  const isViewer = useIsViewer();
  const { sortConfig, requestSort } = useSortable("date", "desc");
  // List state
  const [entries, setEntries] = useState<FeedProduction[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [warnings, setWarnings] = useState<string[]>([]);

  // Shared
  const [formulas, setFormulas] = useState<FeedFormula[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Detail expansion
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [expandedProd, setExpandedProd] = useState<FeedProduction | null>(null);
  const [expandLoading, setExpandLoading] = useState(false);

  // Filters
  const [filterFormulaId, setFilterFormulaId] = useState("");
  const [filterFrom, setFilterFrom] = useState(() => getDefaultDateRange().from);
  const [filterTo, setFilterTo] = useState(() => getDefaultDateRange().to);

  // Form state
  const [formulaId, setFormulaId] = useState("");
  const [date, setDate] = useState(todayISO());
  const [batchSizeKg, setBatchSizeKg] = useState("");
  const [notes, setNotes] = useState("");

  // Edit state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<FeedProduction | null>(null);
  const [editDate, setEditDate] = useState("");
  const [editFormulaId, setEditFormulaId] = useState("");
  const [editBatchSizeKg, setEditBatchSizeKg] = useState("");
  const [editNotes, setEditNotes] = useState("");

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState<FeedProduction | null>(null);
  const [deleting, setDeleting] = useState(false);


  const loadEntries = async (fromDate?: string, toDate?: string, newOffset?: number) => {
    setError("");
    try {
      const o = newOffset !== undefined ? newOffset : offset;
      const params: Record<string, string | number> = { limit, offset: o };
      if (filterFormulaId) params.formula_id = parseInt(filterFormulaId);
      const f = fromDate !== undefined ? fromDate : filterFrom;
      const t = toDate !== undefined ? toDate : filterTo;
      if (f) params.from = f;
      if (t) params.to = t;
      params.sort_by = sortConfig.key;
      params.sort_order = sortConfig.direction;
      const res = await feedProductionService.list(params);
      if (Array.isArray(res)) {
        setEntries(res as unknown as FeedProduction[]);
        setTotal((res as unknown as FeedProduction[]).length);
      } else {
        const paginated = res as unknown as {
          data: FeedProduction[];
          meta: { total: number };
        };
        setEntries(paginated.data ?? []);
        setTotal(paginated.meta?.total ?? 0);
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const loadFormulas = async () => {
    try {
      const data = await feedFormulaService.list();
      const list = Array.isArray(data) ? data : (data as unknown as { data: FeedFormula[] }).data ?? [];
      setFormulas(list.filter((f) => f.active));
    } catch {
      // silently fail
    }
  };

  useEffect(() => {
    loadFormulas();
    loadEntries();
  }, []);

  useEffect(() => {
    setLoading(true);
    loadEntries();
  }, [sortConfig]);

  const openEdit = (entry: FeedProduction) => {
    setEditTarget(entry);
    setEditDate(typeof entry.date === "string" ? entry.date.split("T")[0] : new Date(entry.date).toISOString().split("T")[0]);
    setEditFormulaId(entry.formula_id.toString());
    setEditBatchSizeKg(entry.batch_size_kg.toString());
    setEditNotes(entry.notes || "");
    setError("");
    setWarnings([]);
    setEditModalOpen(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editTarget) return;
    setSaving(true);
    setError("");
    try {
      const result = await feedProductionService.update(editTarget.id, {
        formula_id: parseInt(editFormulaId),
        date: editDate,
        batch_size_kg: parseFloat(editBatchSizeKg),
        notes: editNotes || undefined,
      });
      if (result.warnings && result.warnings.length > 0) {
        setWarnings(result.warnings);
      }
      setEditModalOpen(false);
      setEditTarget(null);
      loadEntries();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await feedProductionService.delete(deleteTarget.id);
      setDeleteTarget(null);
      loadEntries();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  const openCreate = () => {
    const activeFormulas = formulas.filter((f) => f.active);
    setFormulaId(activeFormulas.length > 0 ? activeFormulas[0].id.toString() : "");
    setDate(todayISO());
    setBatchSizeKg("");
    setNotes("");
    setError("");
    setWarnings([]);
    setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setWarnings([]);
    const payload = {
      formula_id: parseInt(formulaId),
      date,
      batch_size_kg: parseFloat(batchSizeKg),
      notes: notes || undefined,
    };

    try {
      const result = await feedProductionService.produce(payload);

      // Show any stock warnings (insufficient/negative balances)
      if (result.warnings && result.warnings.length > 0) {
        setWarnings(result.warnings);
      }

      setModalOpen(false);
      setLoading(true);
      await loadEntries();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };


  const handleDateRangeApply = (from: string, to: string) => {
    setFilterFrom(from);
    setFilterTo(to);
    setOffset(0);
    setLoading(true);
    loadEntries(from, to, 0);
  };

  const handleFilter = () => {
    setOffset(0);
    setLoading(true);
    loadEntries(undefined, undefined, 0);
  };

  // A dropdown applies the moment it changes; there is no separate Filter button.
  useDidUpdate(handleFilter, [filterFormulaId]);

  const handlePrev = () => {
    const newOffset = Math.max(0, offset - limit);
    setOffset(newOffset);
    loadEntries(undefined, undefined, newOffset);
  };

  const handleNext = () => {
    const newOffset = offset + limit;
    setOffset(newOffset);
    loadEntries(undefined, undefined, newOffset);
  };

  const toggleExpand = async (id: number) => {
    if (expandedId === id) {
      setExpandedId(null);
      setExpandedProd(null);
      return;
    }

    setExpandedId(id);
    setExpandLoading(true);
    try {
      const res = await feedProductionService.get(id);
      setExpandedProd(res);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setExpandLoading(false);
    }
  };

  return (
    <>
      <PageMeta
        title="Feed Production | Poultry Book"
        description="Feed production and cost tracking"
      />
      <PageBreadcrumb pageTitle="Feed Production" />

      {/* Warnings banner (shown after successful production with negative stock) */}
      {warnings.length > 0 && (
        <div className="mb-4 rounded-lg bg-warning-50 px-4 py-3 dark:bg-warning-500/10">
          <p className="text-sm font-medium text-warning-600 dark:text-warning-400">
            Production completed with warnings:
          </p>
          <ul className="mt-1 list-disc pl-5 text-sm text-warning-600 dark:text-warning-400">
            {warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="card">
        <div className="flex items-center justify-between px-5 py-4 sm:px-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            Production Records{" "}
            {total > 0 && (
              <span className="text-sm font-normal text-gray-500">
                ({total})
              </span>
            )}
          </h3>
          {!isViewer && (
            <Button size="sm" onClick={openCreate}>
              Produce Feed
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 px-5 pb-4 sm:px-6">
          <select
            value={filterFormulaId}
            onChange={(e) => setFilterFormulaId(e.target.value)}
            className={`${selectClass} max-w-[200px]`}
          >
            <option value="">All Formulas</option>
            {formulas.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
          <DateRangeFilter onApply={handleDateRangeApply} />
        </div>

        {error && !modalOpen && (
          <div className="mx-5 mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500 sm:mx-6">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          {loading ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
              Loading...
            </div>
          ) : entries.length === 0 && !error ? (
            <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
              No feed production in the selected period.
            </div>
          ) : entries.length === 0 ? null : (
            <Table>
              <TableHeader>
                <TableRow className="border-t border-gray-200 dark:border-gray-800">
                  <SortableHeader label="Date" sortKey="date" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableHeader label="Formula" sortKey="formula" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableHeader label="Batch Size" sortKey="quantity" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableHeader label="Total Cost" sortKey="total_cost" sortConfig={sortConfig} onSort={requestSort} />
                  <TableCell
                    isHeader
                    className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400"
                  >
                    Produced By
                  </TableCell>
                  <TableCell
                    isHeader
                    className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400"
                  >
                    Notes
                  </TableCell>
                  {!isViewer && (
                    <TableCell
                      isHeader
                      className="px-5 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400"
                    >
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry) => (
                  <Fragment key={entry.id}>
                    <tr
                      className="border-t border-gray-100 cursor-pointer hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800/50"
                      onClick={() => toggleExpand(entry.id)}
                    >
                      <td className="px-5 py-3 text-sm text-gray-800 dark:text-white/90">
                        {new Date(entry.date).toLocaleDateString()}
                      </td>
                      <td className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {entry.formula_name}
                      </td>
                      <td className="px-5 py-3 text-sm text-right font-medium text-gray-800 dark:text-white/90">
                        {entry.batch_size_kg.toLocaleString()} kg
                      </td>
                      <td className="px-5 py-3 text-sm text-right text-gray-600 dark:text-gray-400">
                        {entry.total_cost != null
                          ? `₹${entry.total_cost.toLocaleString()}`
                          : "-"}
                      </td>
                      <td className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {entry.producer_name || "-"}
                      </td>
                      <td className="px-5 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {entry.notes || "-"}
                      </td>
                      {!isViewer && (
                        <td className="px-5 py-3 text-right">
                          <button
                            onClick={(e) => { e.stopPropagation(); openEdit(entry); }}
                            className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
                          >
                            Edit
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setDeleteTarget(entry); }}
                            className="ml-3 text-sm text-error-500 hover:text-error-600 dark:text-error-400"
                          >
                            Delete
                          </button>
                        </td>
                      )}
                    </tr>
                    {expandedId === entry.id && (
                      <tr
                        key={`${entry.id}-detail`}
                        className="border-t border-gray-100 dark:border-gray-800"
                      >
                        <td className="px-5 py-4 bg-gray-50/50 dark:bg-gray-800/30" colSpan={isViewer ? 6 : 7}>
                          {expandLoading ? (
                            <div className="text-sm text-gray-500">
                              Loading items...
                            </div>
                          ) : expandedProd?.items &&
                            expandedProd.items.length > 0 ? (
                            <div>
                              <p className="mb-2 text-xs font-medium uppercase text-gray-400">
                                Material Breakdown
                              </p>
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="text-left text-gray-500 dark:text-gray-400">
                                    <th className="pb-1 font-medium">
                                      Material
                                    </th>
                                    <th className="pb-1 text-right font-medium">
                                      Qty Used
                                    </th>
                                    <th className="pb-1 text-right font-medium">
                                      Rate
                                    </th>
                                    <th className="pb-1 text-right font-medium">
                                      Cost
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {expandedProd.items.map((item) => (
                                    <tr
                                      key={item.id}
                                      className="border-t border-gray-100 dark:border-gray-700"
                                    >
                                      <td className="py-1.5 text-gray-700 dark:text-gray-300">
                                        {item.raw_material_name}
                                      </td>
                                      <td className="py-1.5 text-right text-gray-700 dark:text-gray-300">
                                        {formatQty(item.qty_used, item.unit)}{" "}
                                        {displayUnit(item.unit)}
                                      </td>
                                      <td className="py-1.5 text-right text-gray-500 dark:text-gray-400">
                                        {item.rate_per_unit != null
                                          ? `₹${formatRate(item.rate_per_unit, item.unit)}/${displayUnit(item.unit)}`
                                          : "-"}
                                      </td>
                                      <td className="py-1.5 text-right text-gray-700 dark:text-gray-300">
                                        {item.item_cost != null
                                          ? `₹${item.item_cost.toLocaleString()}`
                                          : "-"}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500">
                              No items found
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <Pagination total={total} limit={limit} offset={offset} onPrev={handlePrev} onNext={handleNext} />
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        className="max-w-md p-5 sm:p-8 mx-4"
      >
        <h4 className="mb-5 text-lg font-semibold text-gray-800 dark:text-white/90">
          Produce Feed Batch
        </h4>

        {error && modalOpen && (
          <div className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="fpDate">Date *</Label>
            <Input
              id="fpDate"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </div>

          <div>
            <Label htmlFor="fpFormula">Feed Formula *</Label>
            <select
              id="fpFormula"
              value={formulaId}
              onChange={(e) => setFormulaId(e.target.value)}
              required
              className={selectClass}
            >
              <option value="">Select formula</option>
              {formulas.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                  {f.target_stage ? ` (${f.target_stage})` : ""}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="fpBatchSize">Batch Size (kg) *</Label>
            <Input
              id="fpBatchSize"
              type="number"
              value={batchSizeKg}
              onChange={(e) => setBatchSizeKg(e.target.value)}
              placeholder="e.g. 1000"
              required
              min={0.001}
              step="0.001"
            />
          </div>

          <BatchPreview formulaId={formulaId} batchSizeKg={batchSizeKg} />

          <div>
            <Label htmlFor="fpNotes">Notes</Label>
            <textarea
              id="fpNotes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes"
              rows={2}
              className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={saving || !formulaId || !batchSizeKg}>
              {saving ? "Producing..." : "Produce"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        className="max-w-md p-5 sm:p-8 mx-4"
      >
        <h4 className="mb-5 text-lg font-semibold text-gray-800 dark:text-white/90">
          Edit Feed Production
        </h4>

        {error && editModalOpen && (
          <div className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
            {error}
          </div>
        )}

        <form onSubmit={handleEditSubmit} className="space-y-4">
          <div>
            <Label htmlFor="editDate">Date *</Label>
            <Input
              id="editDate"
              type="date"
              value={editDate}
              onChange={(e) => setEditDate(e.target.value)}
              required
            />
          </div>

          <div>
            <Label htmlFor="editFormula">Feed Formula *</Label>
            <select
              id="editFormula"
              value={editFormulaId}
              onChange={(e) => setEditFormulaId(e.target.value)}
              required
              className={selectClass}
            >
              <option value="">Select formula</option>
              {formulas.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                  {f.target_stage ? ` (${f.target_stage})` : ""}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="editBatchSize">Batch Size (kg) *</Label>
            <Input
              id="editBatchSize"
              type="number"
              value={editBatchSizeKg}
              onChange={(e) => setEditBatchSizeKg(e.target.value)}
              placeholder="e.g. 1000"
              required
              min={0.001}
              step="0.001"
            />
          </div>

          <div>
            <Label htmlFor="editNotes">Notes</Label>
            <textarea
              id="editNotes"
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              placeholder="Optional notes"
              rows={2}
              className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" size="sm" onClick={() => setEditModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={saving || !editFormulaId || !editBatchSizeKg}>
              {saving ? "Saving..." : "Update"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Delete Production"
        message={
          <>
            <p className="mb-2">Are you sure you want to delete this feed production entry?</p>
            {deleteTarget && (
              <p className="mb-5 text-gray-500 dark:text-gray-400">
                {deleteTarget.formula_name} &middot; {deleteTarget.batch_size_kg.toLocaleString()} kg &middot; {new Date(deleteTarget.date).toLocaleDateString()}
              </p>
            )}
            <p className="text-xs text-warning-600 dark:text-warning-400">
              Stock consumed by this production will be reversed.
            </p>
            {error && deleteTarget && (
              <div className="mt-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
                {error}
              </div>
            )}
          </>
        }
        busy={deleting}
        busyLabel="Deleting…"
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </>
  );
}
